/* eslint-disable @typescript-eslint/prefer-for-of */
import {Injectable} from '@angular/core';
import {WebPortalMessage} from '@app/models/webportal/Message';
import {ResponseStatus, SourceApp, VcrCommand, WebPortalMessageType} from '@app/models/webportal/types';
import {Store} from '@ngxs/store';
import {LocalUser, LocalUserAction, LocalUserState} from '@app/state/local-user.state';
import {AccessType, LogLevel, LogResource, ViewportTab} from '@app/modules/shared/data/constants';
import {LogService} from '@app/services/log.service';
import {GroupService} from '@app/services/group.service';
import {ClassroomAction, ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {concatMap, map} from 'rxjs/operators';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {AppletURL, AssetHTML, AssetURL, ResourceType} from '@app/models/webportal/Asset';
import {AssignmentState, AssignmentStateModel} from '@app/state/assignment.state';
import {CurrentClass, CurrentClassAdditional} from '@app/models/current-class';
import {CoreService} from '@app/services/core.service';
import {AssignmentService} from '@app/modules/whiteboard/services/assignment.service';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {PrivateWhiteboardService} from '@app/modules/whiteboard/services/private-whiteboard.service';
import {GeoGebraService} from '@app/modules/whiteboard/services/geo-gebra.service';
import {AssetType} from '@app/models/asset-type';
import {Lesson} from '@app/models/vcr-entity/lesson';


@Injectable({
  providedIn: 'root'
})
export class WebPortalMessagingService {
  public readonly showLessonLoadError$ = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private API_VERSION = '1.0';
  private events: { [key: string]: MessageEvent<WebPortalMessage> } = {};
  private isReady = false;
  private localUser: LocalUser;
  private classroom: ClassroomStateModel;
  private assignment: AssignmentStateModel;
  private errorMessage = '';

  constructor(
    private http: HttpClient,
    private store: Store,
    private groupService: GroupService,
    private coreService: CoreService,
    private whiteboardService: WhiteboardService,
    private geoGebraService: GeoGebraService,
    private privateWhiteboardService: PrivateWhiteboardService,
    private assignmentService: AssignmentService
  ) {
    this.listenStoreChanges();
  }

  public init(): void {
    this.registerEventListeners();
  }

  private registerEventListeners(): void {
    window.addEventListener('message', (event: MessageEvent<WebPortalMessage>) => {
      if (event.data.apiVersion && event.data.apiVersion === this.API_VERSION && event.origin === event.data.sourceOrigin) {
        this.handleCommand(event);
      } else {
        console.log('Different origins');
      }
    }, false);
  }

  public setIsReady(isReady: boolean): void {
    this.isReady = isReady;
  }

  private handleCommand(event: MessageEvent<WebPortalMessage>): void {
    const key: string = WebPortalMessagingService.generateMessageKey(event.data);
    this.events[key] = event;
    const message = event.data;
    LogService.writeLog(`Web portal command received type: ${message.command} parameters ${JSON.stringify(message.parameters)}`, LogLevel.DEBUG, LogResource.CORE);
    switch (message.command) {
      case VcrCommand.PingVcr:
        this.pingVcr(message);
        break;
      case VcrCommand.SelectGroup:
        this.selectGroup(message);
        break;
      case VcrCommand.LoadAssignment:
        this.loadAssignment(message);
        break;
      case VcrCommand.UnloadAssignment:
        this.unloadAssignment(message);
        break;
      case VcrCommand.ShareProblemById:
        this.shareProblemById(message);
        break;
      case VcrCommand.ShareProblemByURL:
        this.shareProblemByURL(message);
        break;
      case VcrCommand.OpenAssignment:
        this.openAssignment(message);
        break;
      case VcrCommand.GradeAssignment:
        this.gradeAssignment(message);
        break;
      case VcrCommand.GradeAndCloseAssignment:
        this.gradeAndCloseAssignment(message);
        break;
      case VcrCommand.PublishAssignment:
        this.publishAssignment(message);
        break;
      case VcrCommand.UnpublishAssignment:
        this.unpublishAssignment(message);
        break;
    }

  }

  private pingVcr(message: WebPortalMessage): void {
    if (this.isReady) {
      this.notifyAfterResponseReceived(VcrCommand.PingVcr, message.requestId);
    } else {
      this.notifyAfterEventHappened(VcrCommand.VcrWindowIsLoaded, JSON.stringify({}));
    }
  }

  public selectGroup(message: WebPortalMessage): void {
    let lessonName = message.parameters.lesson ? message.parameters.lesson.toString() : '';
    let lessonId = message.parameters.lessonId;
    let topic = message.parameters.topic ? message.parameters.topic.toString() : '';
    let groupId = message.parameters.groupId;

    if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
      if (groupId > 0) {
        this.groupService.getCorrectLessonToLoad(groupId)
          .toPromise()
          .catch((error) => {
            console.warn(`No lesson found for current time. Loading given lesson by web portal. ${error}`);
          })
          .then((lesson: Lesson): void => {
            lessonName = 'Lesson ' + lesson.lessonNumber;
            lessonId = lesson.id;
            topic = lesson.title;
            groupId = lesson.group.id;
          })
          .catch((error) => {
            console.warn(`Error while overriding Staff Portal lesson. ${error}`);
          })
          .finally(() => {
            this.groupService.getGroupInfoById(groupId)
              .subscribe((groupInfo) => {
                const groupName = this.groupService.extractFromGroupNameYearAndBranch(groupInfo.title);
                const classInfo = new CurrentClass(groupId, groupName, new CurrentClassAdditional(groupInfo.title, lessonName, lessonId, topic, undefined, message.requestId));
                this.coreService.sendSetClass(classInfo)
                  .subscribe({
                    next: () => {
                      this.showLessonLoadError$.next(false);
                      this.coreService.sendToggleDND(!!classInfo.groupId).subscribe();
                    },
                    error: (err) => {
                      console.warn(err);
                      LogService.writeLog(err, LogLevel.WARN, LogResource.CORE);
                      if (this.localUser.accessType === AccessType.TEACHER) {
                        this.showLessonLoadError$.next(true);
                      }
                    }
                  });
                this.notifyAfterResponseReceived(VcrCommand.SelectGroup, message.requestId);
              }, error => {
                LogService.writeLog(error, LogLevel.ERROR, LogResource.CORE);
                this.notifyAfterRequestFailed(VcrCommand.SelectGroup, message.requestId, this.errorMessage);
              });
          });
      } else {
        this.notifyAfterResponseReceived(VcrCommand.SelectGroup, message.requestId);
      }
    } else {
      LogService.writeLog('Prevent load assignment from Web portal for observer', LogLevel.WARN, LogResource.CORE);
      this.errorMessage = 'Prevent load assignment from Web portal for observer';
      this.notifyAfterRequestFailed(VcrCommand.SelectGroup, message.requestId, this.errorMessage);
    }
  }

  public sendLoadAssignment(assignmentId: number): void {
    this.notifyAfterEventHappened(VcrCommand.LoadAssignment, JSON.stringify({groupAssignmentId: assignmentId}));
  }

  public sendUnloadAssignment(): void {
    this.notifyAfterEventHappened(VcrCommand.UnloadAssignment, JSON.stringify({}));
  }

  private loadAssignment(message: WebPortalMessage): void {
    const groupAssignmentId = message.parameters.groupAssignmentId.toString();
    const requestId = message.requestId.toString();

    if (this.localUser && (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT)) {
      this.assignmentService.sendUnload(false).pipe(concatMap(() => {
        this.whiteboardService.sendUnshare();
        return this.assignmentService.sendLoad(groupAssignmentId);
      })).subscribe(value => {
        this.notifyAfterResponseReceived(VcrCommand.LoadAssignment, parseInt(requestId));
      }, error => {
        const errorMessage = 'Error loading assignment. caught: ' + error;
        LogService.writeLog(errorMessage, LogLevel.ERROR, LogResource.ASSIGNMENT);
        this.notifyAfterRequestFailed(VcrCommand.LoadAssignment, requestId, error);
      });
    }
  }

  private unloadAssignment(message: WebPortalMessage): void {
    this.assignmentService.sendUnload().subscribe();
    this.notifyAfterResponseReceived(VcrCommand.UnloadAssignment, message.requestId);
  }

  public shareProblemById(message: WebPortalMessage): void {
    if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
      const assets: any[] = message.parameters.asset;
      const assetType: AssetType = message.parameters.assetType;
      if (assets?.length > 0) {
        const assetIds: {[key: number]: number[]} = {};

        assets.forEach((asset: any): void => {
          assetIds[asset.id] = asset.subProblem || null; /* asset.subProblem contains sub problems ids list. [1754001, 1754002, 1754003] */
        });

        const getAssetURLRequest: Observable<AssetURL[]> =
            assetType ?
            this.getAssetURLsForType(assetIds, assetType) :
            this.getAssetURLsForType(assetIds, AssetType.html);

        getAssetURLRequest.subscribe((assetURLs: AssetURL[]): void => {
          if (assetURLs[0]?.resourceInfo?.type === ResourceType.GEOGEBRA_TEXT) {
            GeoGebraService.assetURLsToAppletURLs(assetURLs)
              .catch((reason): void => {
                console.error(reason);
                throw Error(reason);
              })
              .then((appletURLs: AppletURL[]): void => {
                appletURLs.forEach((appletURL: AppletURL): void => {
                  this.geoGebraService.addGGBApplet(appletURL);
                });
              });
          } else {
            WhiteboardService.assetURLsToAssetHTMLs(assetURLs)
              .catch((reason): void => {
                console.error(reason);
                throw Error(reason);
              })
              .then((assetHTMLs: AssetHTML[]): void => {
                assetHTMLs.forEach((assetHTML: AssetHTML): void => {
                  if (this.classroom.privateTalkInfo.isPrivateWhiteboardOn) {
                    this.privateWhiteboardService.addProblem(assetHTML);
                  } else {
                    this.whiteboardService.addProblem(assetHTML);
                  }
                });
              });
          }
          this.notifyAfterResponseReceived(VcrCommand.ShareProblemById, message.requestId);
          this.store.dispatch(new ClassroomAction.SetBoardTab(ViewportTab.WHITEBOARD));
          this.store.dispatch(new LocalUserAction.SetCurrentTab(ViewportTab.WHITEBOARD));
        }, (error: string): void => {
          const errorMessage = 'Can\'t load asset content: Error: ' + error;
          this.notifyAfterRequestFailed(VcrCommand.ShareProblemById, message.requestId, 'Can\'t load asset content: Error: ' + error);
          LogService.writeLog(errorMessage, LogLevel.ERROR, LogResource.WHITEBOARD);
        });
      } else {
        const error = 'Can not share problem, invalid assetId provided.';
        LogService.writeLog(error, LogLevel.ERROR, LogResource.WHITEBOARD);
        this.notifyAfterRequestFailed(VcrCommand.ShareProblemById, message.requestId, error);
      }
    } else {
      const error = 'Prevent load assignment from Web portal for observer';
      LogService.writeLog(error, LogLevel.WARN, LogResource.WHITEBOARD);
      this.notifyAfterRequestFailed(VcrCommand.ShareProblemById, message.requestId, error);
    }
  }

  public shareProblemByURL(message: WebPortalMessage): void {
    if (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT) {
      if (message.parameters.assetURLs?.length > 0) {
        WhiteboardService.assetURLsToAssetHTMLs(message.parameters.assetURLs).then((assetHTMLs: AssetHTML[]) => {
          assetHTMLs.forEach((assetHTML: AssetHTML): void => {
            if (this.classroom.privateTalkInfo.isPrivateWhiteboardOn) {
              this.privateWhiteboardService.addProblem(assetHTML);
            } else {
              this.whiteboardService.addProblem(assetHTML);
            }
          });
        });
        this.notifyAfterResponseReceived(VcrCommand.ShareProblemByURL, message.requestId);
      } else {
        const error = 'Can not share problem, invalid URLs provided.';
        LogService.writeLog(error, LogLevel.ERROR, LogResource.WHITEBOARD);
        this.notifyAfterRequestFailed(VcrCommand.ShareProblemByURL, message.requestId, error);
      }
    } else {
      const error = 'Prevent load assignment from Web portal for observer';
      LogService.writeLog(error, LogLevel.WARN, LogResource.WHITEBOARD);
      this.notifyAfterRequestFailed(VcrCommand.ShareProblemByURL, message.requestId, error);
    }
  }

  private getAssetURLsForType(assetIds: {[key: number]: number[]}, assetType: AssetType): Observable<AssetURL[]> {
    return this.http.post<AssetURL[]>(`${this.classroom.vcrURL}/asset/${assetType}`, assetIds);
  }

  private getAssetURLs(assetIds: {[key: number]: number[]}): Observable<AssetURL[]> {
    return this.http.post<AssetURL[]>(`${this.classroom.vcrURL}/asset/`, assetIds);
  }

  private openAssignment(message: WebPortalMessage): void {
    if (this.localUser && (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT)) {
      this.refreshStudentAssignment(message.requestId, VcrCommand.OpenAssignment);
    } else {
      const error = 'Can`t refresh assignment access denied';
      LogService.writeLog(error, LogLevel.WARN, LogResource.ASSIGNMENT);
      this.notifyAfterRequestFailed(VcrCommand.OpenAssignment, message.requestId, error);
    }
  }

  public gradeAssignment(message: WebPortalMessage): void {
    this.refreshAssignment(message.requestId, VcrCommand.GradeAssignment);
  }

  private gradeAndCloseAssignment(message: WebPortalMessage): void {
    if (this.localUser && (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT)) {
      this.sendUnloadAssignmentIfLoaded().subscribe(value => {
        this.notifyAfterResponseReceived(VcrCommand.GradeAndCloseAssignment, message.requestId);
      }, error => {
        this.notifyAfterRequestFailed(VcrCommand.GradeAndCloseAssignment, message.requestId, error);
      });
    } else {
      const error = 'Can`t refresh assignment access denied';
      LogService.writeLog(error, LogLevel.WARN, LogResource.ASSIGNMENT);
      this.notifyAfterRequestFailed(VcrCommand.GradeAndCloseAssignment, message.requestId, error);
    }
  }

  private publishAssignment(message: WebPortalMessage): void {
    if (this.localUser && (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT)) {
      if (this.assignment.loadedAssignmentId === message.parameters.groupAssignmentId) {
        this.refreshAssignment(message.requestId, VcrCommand.PublishAssignment);
      }
    } else {
      const error = 'Can`t refresh assignment access denied';
      LogService.writeLog(error, LogLevel.WARN, LogResource.ASSIGNMENT);
      this.notifyAfterRequestFailed(VcrCommand.PublishAssignment, message.requestId, error);
    }
  }

  private unpublishAssignment(message: WebPortalMessage): void {
    if (this.localUser && (this.localUser.accessType === AccessType.TEACHER || this.localUser.accessType === AccessType.SUPPORT)) {
      if (this.assignment.loadedAssignmentId === message.parameters.groupAssignmentId) {
        this.refreshAssignment(message.requestId, VcrCommand.UnpublishAssignment);
      }
    } else {
      const error = 'Can`t refresh assignment access denied';
      LogService.writeLog(error, LogLevel.WARN, LogResource.ASSIGNMENT);
      this.notifyAfterRequestFailed(VcrCommand.UnpublishAssignment, message.requestId, error);
    }
  }

  private static postMessage(event: MessageEvent<WebPortalMessage>, data: any): void {
    const parentWindow: Window = event?.source || window.opener;

    if (parentWindow) {
      parentWindow.postMessage(data, '*');
    } else {
      console.warn('Can\'t send event to Staff Portal as can\'t find reference', event, data);
    }
  }

  private notifyAfterRequestFailed(command: VcrCommand, requestId, error): void {
    const event: MessageEvent<WebPortalMessage> = this.extractEvent(command, requestId);
    const data: any = this.buildResponseData(event);
    data.responseStatus = ResponseStatus.Error;
    data.error = error;

    WebPortalMessagingService.postMessage(event, data);
    this.cleanStore(command, requestId);
  }

  private notifyAfterResponseReceived(command: VcrCommand, requestId: number, parameters?: object): void {
    const event: MessageEvent<WebPortalMessage> = this.extractEvent(command, requestId);

    if (event) {
      const data = this.buildResponseData(event, parameters);
      data.responseStatus = ResponseStatus.Success;

      WebPortalMessagingService.postMessage(event, data);
      this.cleanStore(command, requestId);
    }
  }

  private notifyAfterEventHappened(command: VcrCommand, parametersJson: string): void {
    const event: MessageEvent<WebPortalMessage> = this.events[VcrCommand.PingVcr]; // Take caller window reference (Web-Portal/AssignmentMgr).
    const data: any = this.buildEventData(command, JSON.parse(parametersJson));

    WebPortalMessagingService.postMessage(event, data);
  }

  private static generateMessageKey(message: WebPortalMessage): string {
    if (message.command === VcrCommand.PingVcr || message.messageType === WebPortalMessageType.Event) {
      return message.command;
    } else {
      return message.command + '-' + message.requestId;
    }
  }

  private extractEvent(command: VcrCommand, requestId: number): MessageEvent<WebPortalMessage> {
    const eventKey: string = (command === VcrCommand.PingVcr) ? command : (command + '-' + requestId);
    return this.events[eventKey];
  }

  private cleanStore(command, requestId): void {
    if (command !== VcrCommand.PingVcr) {// remove event data from store except ping event data.
      const eventKey = command + '-' + requestId;
      delete this.events[eventKey];
    }
  }

  private buildEventData(command: VcrCommand, parameters): any {
    return {
      sourceOrigin: window.origin,
      command,
      parameters,
      apiVersion: this.API_VERSION,
      messageType: WebPortalMessageType.Event,
      sourceApp: SourceApp.Vcr,
      loginEmail: this.localUser && this.localUser.email ? this.localUser.email : '', //web portal user login email.
      vcrCommand: true
    };
  }

  private buildResponseData(event: MessageEvent<WebPortalMessage>, parameters?: object): any {
    let mergedParameters: object;

    if (event.data.parameters) {
      mergedParameters = {...event.data.parameters};
    }
    if (parameters) {
      mergedParameters = {...mergedParameters, ...parameters};
    }

    return {
      sourceOrigin: window.origin,
      command: event.data.command,
      parameters: mergedParameters,
      apiVersion: this.API_VERSION,
      requestId: event.data.requestId,
      messageType: WebPortalMessageType.Response,
      sourceApp: SourceApp.Vcr,
      loginEmail: this.localUser && this.localUser.email ? this.localUser.email : '',
      responseStatus: undefined,
      error: undefined
    };
  }


  private refreshStudentAssignment(requestId, vcrCommand: VcrCommand): void {
    if (this.assignment.loadedAssignmentId > 0) {
      this.assignmentService.sendUsersRefresh().subscribe(value => {
        LogService.writeLog('Assignment refresh command has been sent for student.', LogLevel.INFO, LogResource.ASSIGNMENT);
        this.notifyAfterResponseReceived(vcrCommand, requestId);
      }, error => {
        this.notifyAfterRequestFailed(vcrCommand, requestId, error);
      });
    } else {
      const error = 'Can`t refresh assignment, Assignment is not loaded.';
      LogService.writeLog(error, LogLevel.INFO, LogResource.ASSIGNMENT);
      this.notifyAfterRequestFailed(vcrCommand, requestId, error);
    }
  }

  private refreshAssignment(requestId, vcrCommand: VcrCommand): void {
    if (this.assignment.loadedAssignmentId > 0) {
      this.assignmentService.sendRefresh().subscribe(value => {
        LogService.writeLog('Assignment refresh command has been sent for student.', LogLevel.INFO, LogResource.ASSIGNMENT);
        this.notifyAfterResponseReceived(vcrCommand, requestId);
      }, error => {
        this.notifyAfterRequestFailed(vcrCommand, requestId, error);
      });
    } else {
      const error = 'Can`t refresh assignment, Assignment is not loaded.';
      LogService.writeLog(error, LogLevel.INFO, LogResource.ASSIGNMENT);
      this.notifyAfterRequestFailed(vcrCommand, requestId, error);
    }
  }

  private sendUnloadAssignmentIfLoaded(): Observable<any> {
    if (this.assignment.loadedAssignmentId > 0) {
      return this.assignmentService.sendUnload();
    } else {
      return of(true);
    }
  }

  private listenStoreChanges(): void {
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
    this.store.select(AssignmentState).subscribe((assignment: AssignmentStateModel) => {
      this.assignment = assignment;
    });
  }
}
